import UnloadHandler from "@components/unload_handler.tsx";
import { Body } from "@sprout/typography.tsx";
import { useTranslation } from "react-i18next";
import "./work_preferences_widget.scss";
import { SecondaryButton, TertiaryButton } from "@sprout/buttons";
import CompletionTag from "@components/completion_tag.tsx";
import { CompletionStatus } from "@utils/completion_status.ts";
import { useWorkPreferencesForm } from "@hooks/use_work_preferences_form.ts";
import WorkPreferencesForm from "@components/work_preferences/work_preferences_form.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { Option } from "@sprout/select";

interface Props {
  workPreferencesPath: string;
  employmentTypes: Option[];
  worksites: Option[];
  onDismiss: () => void;
}
const WorkPreferencesWidget = ({
  workPreferencesPath,
  employmentTypes,
  worksites,
  onDismiss,
}: Props) => {
  const { t } = useTranslation("user");
  const form = useWorkPreferencesForm(workPreferencesPath);
  const queryClient = useQueryClient();

  const handleSubmit = async () => {
    form.submit().then((success: boolean) => {
      if (success) {
        queryClient.invalidateQueries({ queryKey: ["profile"] });
      }
    });
  };

  const dismiss = () => {
    localStorage.setItem("workPreferencesWidgetDismissedAt", new Date().toISOString());
    onDismiss();
  };

  return (
    <div className="work-preferences-widget">
      {form.unsavedChanges && <UnloadHandler />}
      <div className="work-preferences-widget__header">
        <div className="work-preferences-widget__title">
          <Body medium>{t("workPreferences.title")}</Body>
          <CompletionTag status={CompletionStatus.NotStarted} />
        </div>
        <Body className="!text-sm">{t("workPreferences.widget.description")}</Body>
      </div>
      <WorkPreferencesForm
        form={form}
        className="work-preferences-widget__inputs"
        languagesHelperText={t("workPreferences.widget.languagesHelperText")}
        worksiteOptions={worksites}
        employmentTypeOptions={employmentTypes}
        locationHelperText={t("workPreferences.widget.locationHelperText")}
      />
      <div className="work-preferences-widget__buttons">
        <SecondaryButton onClick={handleSubmit} loading={form.loading} size="medium">
          {t("workPreferences.save")}
        </SecondaryButton>
        <TertiaryButton onClick={dismiss} secondary>
          {t("workPreferences.widget.dismiss")}
        </TertiaryButton>
        {form.error && (
          <p
            id="work-preferences-error"
            className="helper-text helper-text--error"
            aria-live="polite"
          >
            {form.error}
          </p>
        )}
      </div>
    </div>
  );
};

export default WorkPreferencesWidget;
